import { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, Search, AlertCircle, Trash } from 'react-feather';
import { AppButton } from '../../../general/AppButton/AppButton';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { AppErrorPage } from '../../../general/AppErrorPage/AppErrorPage';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { COLORS } from '../../../../utils/colors';
import {
  getSupplierJobs,
  recursiveGetSupplierJobs,
  updateArchiveStateSupplierJob,
} from '../../../../redux/services/supplierJobsService';
import {
  PrelienStatus,
  PreliminaryNoticeFieldData,
  SupplierJobApiResponse,
} from '@dill/dill-shared';
import SupplierJobsTable from '../SupplierJobsTable/SupplierJobsTable';
import { AppNewSupplierJobModal } from '../../../general/AppNewSupplierJobModal/AppNewSupplierJobModal';
import {
  getSupplierBuyers,
  updateSupplierBuyer,
} from '../../../../redux/services/supplierBuyersService';
import { getSupplierLienTemplates } from '../../../../redux/services/leinTemplatesService';
import { getUserSignatures } from '../../../../redux/services/authService';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { AppLoadingProgress } from '../../../general/AppLoadingProgress/AppLoadingProgress';
import { downloadFile, openDillToast, removeExtraSpaces } from '../../../../utils/helpers';
import {
  resetSelectedSupplierJobs,
  resetSupplierJobs,
} from '../../../../redux/globalSlices/supplierJobsSlice';
import { AppBulkEditSupplierJobModal } from '../../../general/AppNewSupplierJobModal/AppBulkEditSupplierJobModal';
import { AppDropDown2 } from '../../../general/AppDropDown2/AppDropDown2';
import { AppCheckBox } from '../../../general/AppCheckBox/AppCheckBox';
import {
  getRequestedSupplierJobs,
  updateRequestedSupplierJob,
} from '../../../../redux/services/requestJobInformationService';
import AppRequestedJobsModal from '../../../general/AppRequestedJobsModal/AppRequestedJobsModal';
import { AppDropdownButton } from '../../../general/AppDropdownButton/AppDropdownButton';
import { AppRequestInfoJobModal } from '../../../general/AppNewSupplierJobModal/AppRequestInfoJobModal';
import { useLocation } from 'react-router-dom';
import { openMessageModal } from '../../../../redux/globalSlices/genericSlice';
import { AppStandardInfoSheet } from '../../../general/AppNewSupplierJobModal/AppStandardInfoSheet';
import { AppPdfPreviewModal } from '../../../general/AppPdfPreviewModal/AppPdfPreviewModal';
import { AppImagePreviewModal } from '../../../general/AppImagePreviewModal/AppImagePreviewModal';
import { AppBulkPreliminaryModal } from '../../../general/AppBulkPreliminaryModal/AppBulkPreliminaryModal';
import { getNoticeTemplates } from '../../../../redux/services/noticeTemplatesService';
import CreateSignatureModal from '../../lienWaivers/supplier/CreateSignatureModal/CreateSignatureModal';
import { createBulkPreliminaryNotice } from '../../../../redux/services/preliminaryNoticesService';
import MergeSupplierJobsModal from '../MergeSupplierJobsModal/MergeSupplierJobsModal';

enum ModalState {
  ADD_JOB,
  REQUEST_JOB_INFO_SHEET,
}

const SupplierJobs = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [addNewJob, setAddNewJob] = useState(false);
  const [requestJobInfo, setRequestJobInfo] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkEditJob, setBulkEditJob] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [toogle, setToogle] = useState(true);
  const [supplierJobsCount, setSupplierJobsCount] = useState(0);
  const [customerFilterOption, setCustomerFilterOption] = useState<string[] | null>(null);

  const { loadingErrorList, supplierJobsMap, supplierJobsArchivedMap, selectedSupplierJobs } =
    useAppSelector((state) => state.supplierJobs);
  const { requestedSupplierJobs } = useAppSelector((state) => state.requestJobInformation);
  const { lienTemplates } = useAppSelector((state) => state.lienTemplates);
  const { noticeTemplates } = useAppSelector((state) => state.noticeTemplates);
  const { userSignatures, user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  // Tracks which supplier the in-flight recursive job fetch belongs to, so that a chain
  // started for a previous supplier stops as soon as the supplier is switched. `inFlight
  // JobsRequestRef` holds the latest dispatched request so it can be aborted on switch.
  const activeFetchSupplierIdRef = useRef<string | null>(null);
  const inFlightJobsRequestRef = useRef<{ abort: () => void } | null>(null);
  const [sortOptions, setSortOptions] = useState<{ sortOrder: 'asc' | 'desc'; sortBy: string }>({
    sortOrder: 'desc',
    sortBy: 'createdAt',
  });
  const [previousLastDocumentId, setPreviousLastDocumentId] = useState('');
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentSupplierJobs, setCurrentSupplierJobs] = useState<SupplierJobApiResponse[]>([]);
  const [dataFullyLoaded, setDataFullyLoaded] = useState<boolean>(false);
  const [isArchivedJobsFetched, setIsArchivedJobsFetched] = useState<boolean>(false);
  const [openRequestedJobsModal, setOpenRequestedJobsModal] = useState<boolean>(false);
  const [selectedSupplierJob, setSelectedSupplierJob] = useState<SupplierJobApiResponse | null>(
    null
  );
  const [editSupplierJob, setEditSupplierJob] = useState(false);
  const [requestedJobId, setRequestedJobId] = useState('');
  const [generalJobInfo, setGeneralJobInfo] = useState<boolean>(false);
  const [showPreviewPdf, setShowPreviewPdf] = useState(false);
  const [selectedAttachmentUrl, setSelectedAttachmentUrl] = useState('');
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [showPreviewImage, setShowPreviewImage] = useState(false);
  const [bulkRequestPrelimNotices, setBulkRequestPrelimNotices] = useState(false);
  const [isMergeSupplierJobsModalOpen, setIsMergeSupplierJobsModalOpen] = useState(false);
  const [showPreviewPrelimNotices, setShowPreviewPrelimNotices] = useState(false);
  const [prelimPreviewURL, setPrelimPreviewURL] = useState('');
  const [isCreateSignatureModalOpen, setIsCreateSignatureModalOpen] = useState(false);
  const [signature, setSignature] = useState('');

  const searchedSupplierJobs: SupplierJobApiResponse[] = useMemo(() => {
    let filteredSupplierJobs: SupplierJobApiResponse[] = [];
    const activeJobs = [
      ...Object.values(supplierJobsMap),
      ...Object.values(supplierJobsArchivedMap),
    ].filter((job) => {
      if (toogle && job?.isArchived && job?.isArchived) {
        return false;
      }
      return true;
    });
    if (activeJobs && searchText) {
      const searched = activeJobs.filter((supplierJob) => {
        const updateSearchedText = removeExtraSpaces(searchText);
        if (
          (supplierJob.name &&
            removeExtraSpaces(supplierJob.name)
              .toString()
              .toLowerCase()
              .includes(updateSearchedText.toLowerCase())) ||
          (supplierJob.jobNumber &&
            supplierJob.jobNumber
              .toString()
              .toLowerCase()
              .includes(updateSearchedText.toLowerCase())) ||
          (supplierJob.buyer?.name &&
            supplierJob.buyer?.name
              .toString()
              .toLowerCase()
              .includes(updateSearchedText.toLowerCase())) ||
          (supplierJob.buyer?.address &&
            removeExtraSpaces(supplierJob.buyer?.address)
              .toString()
              .toLowerCase()
              .includes(updateSearchedText.toLowerCase())) ||
          (supplierJob.buyer?.city &&
            removeExtraSpaces(supplierJob.buyer?.city)
              .toString()
              .toLowerCase()
              .includes(updateSearchedText.toLowerCase())) ||
          (supplierJob.buyer?.state &&
            removeExtraSpaces(supplierJob.buyer?.state)
              .toString()
              .toLowerCase()
              .includes(updateSearchedText.toLowerCase())) ||
          (supplierJob.buyer?.zipCode &&
            removeExtraSpaces(supplierJob.buyer?.zipCode)
              .toString()
              .toLowerCase()
              .includes(updateSearchedText.toLowerCase())) ||
          (supplierJob.owner &&
            removeExtraSpaces(supplierJob.owner)
              .toString()
              .toLowerCase()
              .includes(updateSearchedText.toLowerCase())) ||
          (supplierJob.ownerAddress &&
            removeExtraSpaces(supplierJob.ownerAddress)
              .toString()
              .toLowerCase()
              .includes(updateSearchedText.toLowerCase())) ||
          (supplierJob.ownerCity &&
            removeExtraSpaces(supplierJob.ownerCity)
              .toString()
              .toLowerCase()
              .includes(updateSearchedText.toLowerCase())) ||
          (supplierJob.ownerState &&
            removeExtraSpaces(supplierJob.ownerState)
              .toString()
              .toLowerCase()
              .includes(updateSearchedText.toLowerCase())) ||
          (supplierJob.ownerZipCode &&
            removeExtraSpaces(supplierJob.ownerZipCode)
              .toString()
              .toLowerCase()
              .includes(updateSearchedText.toLowerCase())) ||
          (supplierJob.jobAddress &&
            removeExtraSpaces(supplierJob.jobAddress)
              .toString()
              .toLowerCase()
              .includes(updateSearchedText.toLowerCase())) ||
          (supplierJob.jobCity &&
            removeExtraSpaces(supplierJob.jobCity)
              .toString()
              .toLowerCase()
              .includes(updateSearchedText.toLowerCase())) ||
          (supplierJob.jobState &&
            removeExtraSpaces(supplierJob.jobState)
              .toString()
              .toLowerCase()
              .includes(updateSearchedText.toLowerCase())) ||
          (supplierJob.jobZipCode &&
            removeExtraSpaces(supplierJob.jobCity)
              .toString()
              .toLowerCase()
              .includes(updateSearchedText.toLowerCase())) ||
          (supplierJob?.GC &&
            removeExtraSpaces(supplierJob.GC)
              .toString()
              .toLowerCase()
              .includes(updateSearchedText.toLowerCase())) ||
          (supplierJob?.GCAddress &&
            removeExtraSpaces(supplierJob.GCAddress)
              .toString()
              .toLowerCase()
              .includes(updateSearchedText.toLowerCase())) ||
          (supplierJob?.GCCity &&
            removeExtraSpaces(supplierJob.GCCity)
              .toString()
              .toLowerCase()
              .includes(updateSearchedText.toLowerCase())) ||
          (supplierJob?.GCState &&
            removeExtraSpaces(supplierJob.GCState)
              .toString()
              .toLowerCase()
              .includes(updateSearchedText.toLowerCase())) ||
          (supplierJob?.apn &&
            removeExtraSpaces(supplierJob.apn)
              .toString()
              .toLowerCase()
              .includes(updateSearchedText.toLowerCase())) ||
          (supplierJob?.GCZipCode &&
            removeExtraSpaces(supplierJob.GCZipCode)
              .toString()
              .toLowerCase()
              .includes(updateSearchedText.toLowerCase())) ||
          (supplierJob?.estimatedMaterialAmount &&
            removeExtraSpaces(String(supplierJob.estimatedMaterialAmount))
              .toString()
              .toLowerCase()
              .includes(updateSearchedText.toLowerCase()))
        ) {
          return true;
        }
        return false;
      });
      filteredSupplierJobs = searched;
    } else {
      filteredSupplierJobs = activeJobs;
    }

    if (customerFilterOption && customerFilterOption.length > 0) {
      filteredSupplierJobs = filteredSupplierJobs.filter((supplierJob) =>
        customerFilterOption.includes(supplierJob.buyerId)
      );
    }

    return filteredSupplierJobs;
  }, [supplierJobsMap, searchText, toogle, customerFilterOption]);

  const error = useMemo(() => {
    if (loadingErrorList.length > 0) {
      const errorMessage: any = loadingErrorList.filter((obj) =>
        obj.hasOwnProperty('getSupplierJobs')
      );
      if (errorMessage.length > 0) {
        return errorMessage[0]?.getSupplierJobs;
      }
    }
    return null;
  }, [loadingErrorList]);

  const recursiveFetchSupplierJob = async ({
    lastDockId,
    fetchedJobs,
    count,
    supplierId,
  }: {
    count: number;
    lastDockId: string;
    fetchedJobs: SupplierJobApiResponse[];
    supplierId: string;
  }) => {
    // A newer fetch (e.g. after switching suppliers) supersedes this chain — stop so we
    // don't write the previous supplier's jobs into the store.
    if (!supplierId || activeFetchSupplierIdRef.current !== supplierId) {
      return;
    }
    setSupplierJobsCount(count);
    if (fetchedJobs.length === 0) {
      setLoading(true);
      setDataFullyLoaded(false);
      setSupplierJobsCount(0);
    }
    const pageSize = fetchedJobs.length == 0 ? 50 : 100;
    let currentPreviousLastDocumentId = lastDockId;
    const request = dispatch(
      recursiveGetSupplierJobs({
        supplierId,
        isShowLoadingIndicator: fetchedJobs.length == 0 ? true : false,
        includeInvoices: true,
        includePreLiens: true,
        isOnlyArchivedJobs: !toogle,
        previousLastDocumentId: currentPreviousLastDocumentId,
        pageSize,
        sortBy: sortOptions.sortBy,
        sortOrder: sortOptions.sortOrder,
      })
    );
    inFlightJobsRequestRef.current = request;
    const results = await request;
    // Supplier changed while this page was in flight — abandon the rest of the chain.
    if (activeFetchSupplierIdRef.current !== supplierId) {
      return;
    }
    if (results.type === 'supplierJobs/recursiveGetSupplierJobs/fulfilled') {
      if (results.payload) {
        if (supplierJobsCount === 0) {
          const payload = results.payload as any;
          setSupplierJobsCount(Number(payload.count));
        }
        const payload = results.payload as any;
        const fetchedSupplierJobs: SupplierJobApiResponse[] = payload.data as any;
        const lastDocumentId = payload.lastDocumentId;
        if (lastDocumentId !== currentPreviousLastDocumentId) {
          currentPreviousLastDocumentId = lastDocumentId;
        }
        if (fetchedSupplierJobs.length === pageSize) {
          currentPreviousLastDocumentId = lastDocumentId;
          const jobslist = [...fetchedJobs, ...fetchedSupplierJobs];
          await recursiveFetchSupplierJob({
            lastDockId: lastDocumentId,
            fetchedJobs: jobslist,
            count: Number(payload.count),
            supplierId,
          });
        }
        if (!lastDockId || fetchedSupplierJobs.length < pageSize) {
          console.log('No more pages to fetch');
          setDataFullyLoaded(true);
          if (!toogle) {
            setIsArchivedJobsFetched(true);
          }
        }
      }
    } else {
      setDataFullyLoaded(true);
      console.log('Fetch failed or interrupted');
    }
  };

  const handleLoadMore = async (loadOptions?: { isFreshFetch?: boolean }) => {
    const fetchSupplierId = currentSupplier?.id || '';
    if (!fetchSupplierId) {
      return;
    }
    // If we're now fetching for a different supplier, cancel the previous chain's
    // in-flight request so its result can't merge into the new supplier's jobs.
    if (activeFetchSupplierIdRef.current && activeFetchSupplierIdRef.current !== fetchSupplierId) {
      inFlightJobsRequestRef.current?.abort();
    }
    activeFetchSupplierIdRef.current = fetchSupplierId;
    recursiveFetchSupplierJob({
      count: 0,
      lastDockId: '',
      fetchedJobs: [],
      supplierId: fetchSupplierId,
    });
  };

  useEffect(() => {
    dispatch(resetSelectedSupplierJobs());
    if (user && currentSupplier?.id) {
      dispatch(getRequestedSupplierJobs({ supplierId: currentSupplier?.id || '' }));
      handleLoadMore({ isFreshFetch: true });
      dispatch(getSupplierBuyers({ supplierId: currentSupplier?.id || '' }));
      if (lienTemplates.length === 0) {
        dispatch(getSupplierLienTemplates({ supplierId: currentSupplier?.id || '' }));
      }
      if (noticeTemplates.length === 0) {
        dispatch(getNoticeTemplates({ supplierId: currentSupplier?.id || '' }));
      }
      if (userSignatures.length === 0) {
        dispatch(getUserSignatures());
      }
    }
  }, [user?.id, currentSupplier?.id]);

  useEffect(() => {
    if (!toogle && dataFullyLoaded && !isArchivedJobsFetched) {
      handleLoadMore({ isFreshFetch: true });
    }
  }, [toogle]);

  useEffect(() => {
    if (location.search.includes('refresh=true') && user && currentSupplier) {
      dispatch(resetSelectedSupplierJobs());
      dispatch(getRequestedSupplierJobs({ supplierId: currentSupplier?.id || '' }));
      handleLoadMore({ isFreshFetch: true });
      dispatch(getSupplierBuyers({ supplierId: currentSupplier?.id || '' }));
      if (lienTemplates.length === 0) {
        dispatch(getSupplierLienTemplates({ supplierId: currentSupplier?.id || '' }));
      }
      if (userSignatures.length === 0) {
        dispatch(getUserSignatures());
      }
    }
  }, [location, user?.id, currentSupplier?.id]);

  const [infiniteRef] = useInfiniteScroll({
    loading: loading,
    hasNextPage: hasNextPage,
    onLoadMore: handleLoadMore,
    rootMargin: '0px 0px 400px 0px',
  });

  const customerDropdownList = useMemo(() => {
    const customersMap = new Map();

    Object.values(supplierJobsMap).forEach((job) => {
      if (job.buyer) {
        customersMap.set(job.buyer.id, {
          value: job.buyer.id ?? '',
          label: job.buyer?.name?.toLowerCase() ?? '',
        });
      }
    });

    const customers = Array.from(customersMap.values());

    return customers;
  }, [supplierJobsMap]);

  const handleEditSupplierJob = (data: SupplierJobApiResponse, id: string) => {
    setSelectedSupplierJob(data);
    setEditSupplierJob(true);
    setRequestedJobId(id);
  };

  const handleUpdateRequestedJob = async () => {
    await dispatch(
      updateRequestedSupplierJob({
        supplierId: currentSupplier?.id || '',
        requestedSupplierJobId: requestedJobId,
        requestedSupplierJobDetails: { status: 'approved' },
      })
    );
    await dispatch(getRequestedSupplierJobs({ supplierId: currentSupplier?.id || '' }));
    const currentRequestedJob = requestedSupplierJobs.find((job) => job.id === requestedJobId);
    if (currentRequestedJob) {
      if (currentRequestedJob?.customerAddress?.address !== undefined) {
        await dispatch(
          updateSupplierBuyer({
            supplierId: currentSupplier?.id || '',
            supplierBuyerId: currentRequestedJob?.buyerId || '',
            supplierBuyerDetails: {
              address: currentRequestedJob.customerAddress?.address || '',
              city: currentRequestedJob.customerAddress?.city || '',
              state: currentRequestedJob.customerAddress?.state || '',
              zipCode: currentRequestedJob.customerAddress?.zipCode || '',
            },
          })
        );
        handleLoadMore({ isFreshFetch: true });
      }
    }
  };
  const handleBulkArchiveJobs = async () => {
    if (selectedSupplierJobs.length > 0) {
      dispatch(
        openMessageModal({
          buttonText: 'Yes, I’m sure',
          modalType: 'INFO',
          customIcon: (
            <div className="rounded-full ERROR_100-BG p-6">
              <Trash color={COLORS.ERROR_500} size={52} />
            </div>
          ),
          title: `Are you sure you want to mark the selected jobs as a inactive?`,
          subTitle: `This action will archive the job. This action can't be undone`,
          isSecondButton: true,
          secondButtonText: 'No, cancel',
          onClose: async () => {
            const response = await dispatch(
              updateArchiveStateSupplierJob({
                supplierId: currentSupplier?.id || '',
                supplierJobsIds: selectedSupplierJobs.map((job) => job.id),
                markAs: 'INACTIVE',
              })
            );
            if (response.type === 'supplierJobs/updateArchiveStateSupplierJob/fulfilled') {
              dispatch(resetSelectedSupplierJobs());
              openDillToast({ message: 'Successfully archived jobs', type: 'SUCCESS' });
            } else {
              let errorMessage = '';
              if (response.type === 'supplierJobs/updateArchiveStateSupplierJob/rejected') {
                errorMessage = response.payload as any;
              }
              openDillToast({ message: `${errorMessage}`, type: 'ERROR' });
            }
          },
          onSecondButtonClick: async () => {},
        })
      );
    }
  };
  const handlePreviewAttachment = (url: string, fileType: string) => {
    if (fileType && fileType !== 'pdf') {
      setAttachmentUrl(url);
      setShowPreviewImage(true);
    } else {
      setShowPreviewPdf(true);
      setSelectedAttachmentUrl(url);
    }
  };

  const handleDownloadAttachment = (url: string, name: string) => {
    downloadFile(url, name);
  };

  const handleAddSignature = (newSignature: string, isManual: boolean) => {
    setSignature(newSignature);
    setIsCreateSignatureModalOpen(false);
  };

  const handleBulkCreatePreliminaryNotices = async (
    data: {
      jobId: string;
      previewPdfUrl: string;
      templateId: string;
      fieldsData: PreliminaryNoticeFieldData[];
      supplierId: string;
      status: string;
      ownersStatus: PrelienStatus[];
      contractorsStatus: PrelienStatus[];
      lendersStatus: PrelienStatus[];
      suretiesStatus?: PrelienStatus[];
    }[]
  ) => {
    setBulkSaving(true);
    const createBulkResult = await dispatch(
      createBulkPreliminaryNotice({ data, supplierId: currentSupplier?.id || '' })
    );
    if (createBulkResult.type === 'preliminaryNotices/createBulkPreliminaryNotice/fulfilled') {
      setSignature('');
      setBulkSaving(false);
      setBulkRequestPrelimNotices(false);
      handleLoadMore({ isFreshFetch: true });
      openDillToast({ type: 'SUCCESS', message: 'Preliminary Notices created successfully.' });
    } else {
      setBulkSaving(false);
      openDillToast({ type: 'ERROR', message: 'Preliminary Notices failed to create.' });
    }
  };

  return (
    <div className="h-full p-4 flex ">
      <AppImagePreviewModal
        open={showPreviewImage}
        handleClose={() => {
          setShowPreviewImage(false);
          setAttachmentUrl('');
        }}
        title="Job Info Sheet Attachment"
        url={attachmentUrl}
      />
      <AppNewSupplierJobModal
        open={addNewJob}
        handleClose={() => {
          setAddNewJob(false);
        }}
        onReload={() => {
          handleLoadMore({ isFreshFetch: true });
        }}
      />
      <AppRequestInfoJobModal
        open={requestJobInfo}
        handleClose={() => {
          setRequestJobInfo(false);
        }}
        onReload={() => {
          handleLoadMore({ isFreshFetch: true });
        }}
      />
      <AppNewSupplierJobModal
        open={editSupplierJob}
        handleClose={() => {
          setEditSupplierJob(false);
        }}
        onReload={() => {}}
        editMode={true}
        selectedSupplierJob={selectedSupplierJob}
        onUpdateRequestedJob={handleUpdateRequestedJob}
      />
      <AppBulkEditSupplierJobModal
        open={bulkEditJob}
        handleClose={() => {
          setBulkEditJob(false);
          dispatch(resetSelectedSupplierJobs());
        }}
      />
      <AppRequestedJobsModal
        open={openRequestedJobsModal}
        handleClose={() => {
          setOpenRequestedJobsModal(false);
        }}
        requestedSupplierJobs={requestedSupplierJobs}
        onEditSupplierJob={handleEditSupplierJob}
        onReload={() => {
          handleLoadMore({ isFreshFetch: true });
        }}
        onPreviewAttachment={handlePreviewAttachment}
        onDownloadAttachment={handleDownloadAttachment}
      />
      <AppStandardInfoSheet
        open={generalJobInfo}
        handleClose={() => {
          setGeneralJobInfo(false);
        }}
      />
      <AppPdfPreviewModal
        open={showPreviewPdf}
        handleClose={() => {
          setShowPreviewPdf(false);
        }}
        invoice={''}
        pdfType={'PRELIMINARY_NOTICE'}
        pdfList={[
          {
            name: 'attachment',
            url: selectedAttachmentUrl,
            date: new Date(),
          },
        ]}
        handleDownloadPdf={() => {}}
        isSigned={false}
        showPendingMessage={false}
        mode="Supplier"
        showHistoryButton={false}
      />
      <AppBulkPreliminaryModal
        open={bulkRequestPrelimNotices}
        handleClose={() => {
          setBulkRequestPrelimNotices(false);
          setSignature('');
          dispatch(resetSelectedSupplierJobs());
        }}
        onCreateBulkReleases={handleBulkCreatePreliminaryNotices}
        onPreviewRelease={(url: string) => {
          setPrelimPreviewURL(url);
          setShowPreviewPrelimNotices(true);
        }}
        onSignPreLien={() => {
          setIsCreateSignatureModalOpen(true);
        }}
        signature={signature}
        bulkSaving={bulkSaving}
        onEditSupplierJob={(data: SupplierJobApiResponse) => {
          setSelectedSupplierJob(data);
          setEditSupplierJob(true);
        }}
      />
      <AppPdfPreviewModal
        open={showPreviewPrelimNotices}
        handleClose={() => {
          setShowPreviewPrelimNotices(false);
          setPrelimPreviewURL('');
        }}
        invoice={''}
        pdfType="PRELIMINARY_NOTICE"
        pdfList={[
          {
            name: 'Preliminary Notice',
            url: prelimPreviewURL,
            date: new Date(),
          },
        ]}
        handleDownloadPdf={() => {}}
        isSigned={false}
        mode="Supplier"
      />
      <CreateSignatureModal
        open={isCreateSignatureModalOpen}
        handleClose={() => {
          setIsCreateSignatureModalOpen(false);
        }}
        addSignature={handleAddSignature}
        onDownloadRealease={() => {}}
        defaultIsManualSign={false}
      />
      <MergeSupplierJobsModal
        open={isMergeSupplierJobsModalOpen}
        selectedSupplierJobs={selectedSupplierJobs}
        handleClose={(isRefresh) => {
          setIsMergeSupplierJobsModalOpen(false);
          dispatch(resetSelectedSupplierJobs());

          if (isRefresh) {
            dispatch(getRequestedSupplierJobs({ supplierId: currentSupplier?.id || '' }));
            handleLoadMore({ isFreshFetch: true });
          }
        }}
      />
      <div className="flex flex-col px-5 py-4 w-full bg-white">
        <div className="flex justify-between">
          <div className="flex items-center">
            <div className="flex items-center">
              <AppInputField
                inputFieldStyles={{ width: '300px' }}
                id="search"
                value={searchText}
                placeholder={'Search '}
                icon={<Search size={15} />}
                onTextChange={(text) => {
                  setSearchText(text);
                }}
              />
              {dataFullyLoaded && (
                <div className="flex ml-2">
                  <div
                    onClick={() => setToogle(!toogle)}
                    className={`w-[40px] h-[22px] rounded-full p-[1px] grid flex-col cursor-pointer ${
                      toogle ? 'justify-items-end bg-[#3862fb]' : 'GREY_500-BG'
                    }`}>
                    <div className="bg-white w-[20px] h-[20px] rounded-full" />
                  </div>
                  <div className="w-[180px] ml-2 font-semibold text-sm">Show Only Active Jobs</div>
                </div>
              )}
            </div>
            <div className="w-[220px] relative">
              <AppDropDown2
                value={''}
                isMulti={true}
                placeholder="Filter by Customer"
                isCreatable={false}
                closeMenu={false}
                items={[
                  ...customerDropdownList.map((item) => ({
                    label: item.label,
                    value: item.value,
                    labelInElement: (
                      <div className="flex m-1 items-center">
                        <div className="w-[25px] mr-2">
                          <AppCheckBox isChecked={customerFilterOption?.includes(item.value)} />
                        </div>
                        <div className="text-black text-sm capitalize">{item.label}</div>
                      </div>
                    ),
                  })),
                ]}
                onMultiSelectChange={(data, oldMultiValue) => {
                  const valueList = data.map((item) => item.value);
                  setCustomerFilterOption(valueList);
                }}
                onSelectChange={(item) => {}}
                itemWidth={220}
                multiValue={customerFilterOption}
              />
              {customerFilterOption && customerFilterOption?.length > 0 && (
                <div className="flex absolute bottom-6 right-[-12px] PRIMARY_500-BG WHITE-CLR rounded-full text-xs font-bold h-8 w-8 items-center justify-center">
                  {customerFilterOption?.length}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center">
            <AppLoadingProgress
              text="Supplier Job"
              currentCount={
                toogle && !dataFullyLoaded
                  ? Object.keys(supplierJobsMap).length
                  : !toogle && !dataFullyLoaded
                  ? Object.keys(supplierJobsArchivedMap).length
                  : toogle && dataFullyLoaded
                  ? Object.keys(supplierJobsMap).length
                  : Object.keys(supplierJobsArchivedMap).length +
                    Object.keys(supplierJobsMap).length
              }
              totalCount={
                !dataFullyLoaded
                  ? supplierJobsCount
                  : toogle
                  ? Object.keys(supplierJobsMap).length
                  : Object.keys(supplierJobsMap).length +
                    Object.keys(supplierJobsArchivedMap).length
              }
              showTotalLoading={true}
            />

            {selectedSupplierJobs.length > 0 && (
              <AppDropdownButton
                buttonType="SECONDARY"
                options={[
                  { label: 'Bulk Edit', value: 'bulkEdit' },
                  { label: 'Bulk Archive', value: 'bulkArchive' },
                  { label: 'Bulk Request Preliminary Notices', value: 'bulkRequestNotice' },
                  { label: 'Merge Jobs', value: 'mergeJobs' },
                ]}
                onSelect={(val) => {
                  if (val.value === 'bulkEdit') {
                    setBulkEditJob(true);
                  } else if (val.value === 'bulkArchive') {
                    handleBulkArchiveJobs();
                  } else if (val.value === 'mergeJobs') {
                    setIsMergeSupplierJobsModalOpen(true);
                  } else {
                    setBulkRequestPrelimNotices(true);
                  }
                }}
                buttonTitle="Bulk Action"
                buttonStyles={{ marginLeft: '8px' }}
                contentWidth={220}
              />
            )}
            <AppDropdownButton
              options={[
                { label: 'Add Job Without Request Info Sheet', value: 'withoutRequestInfoSheet' },
                { label: 'Add Job With Request Info Sheet', value: 'withRequestInfoSheet' },
                { label: 'Standard Job Info Sheet Link', value: 'standardJobInfoSheetLink' },
              ]}
              onSelect={(val) => {
                if (val.value === 'withoutRequestInfoSheet') {
                  setAddNewJob(true);
                } else if (val.value === 'withRequestInfoSheet') {
                  setRequestJobInfo(true);
                } else {
                  setGeneralJobInfo(true);
                }
              }}
              buttonTitle="Add New Job"
              icon={<Plus color={COLORS.WHITE} />}
              buttonStyles={{ marginLeft: '8px' }}
              buttonIconPosition="LEFT"
              contentWidth={220}
            />
          </div>
        </div>
        {requestedSupplierJobs.length > 0 && (
          <div className="flex rounded PRIMARY_100-BG PRIMARY_500-CLR p-2 items-center w-fit mt-2">
            <AlertCircle size={18} />
            <div className="mx-2 text-sm">
              {`There ${
                requestedSupplierJobs.length > 1
                  ? `are ${requestedSupplierJobs.length} new jobs`
                  : `is a new job ${requestedSupplierJobs[0].name}`
              } entered by your contractor${requestedSupplierJobs.length > 1 ? 's' : ''}.`}
            </div>
            <div
              onClick={() => setOpenRequestedJobsModal(true)}
              className="underline text-sm cursor-pointer font-semibold">
              Review here
            </div>
          </div>
        )}
        {error ? (
          <AppErrorPage
            title="Error loading  jobs."
            contactMessage="Please contact customer support."
            errorMessage={error}
          />
        ) : (
          <SupplierJobsTable
            searchedSupplierJobs={searchedSupplierJobs}
            infiniteRef={infiniteRef}
            hasNextPage={false}
            loading={loading}
            onReload={() => {
              handleLoadMore({ isFreshFetch: true });
            }}
            dataFullyLoaded={dataFullyLoaded}
            onViewSubmittedJobInfoSheet={() => {
              setOpenRequestedJobsModal(true);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default SupplierJobs;
